/**
 * Adapter hook for creating a new job.
 * Wraps useCreateJobMutation, transforms form data to GraphQL input, and handles crew/extra systems cleanup.
 */
import { JobFormData } from '../types/JobTypes';
import { mapFormDataToCreateInput } from '../mappers/JobMappers';
import { useCreateJobMutation } from '@/common/infrastructure/gql/generated/schema';
import { useJobCrewTemporaryStore } from '@/store/JobCrewTemporaryStore';
import { useJobExtraSystemsTemporaryStore } from '@/store/JobExtraSystemsTemporaryStore';
import { createJobMutationError } from '../types/JobMutationError';

export const useCreateJob = () => {
  const [createJob, { data, loading, error }] = useCreateJobMutation();
  const clearCrewJob = useJobCrewTemporaryStore((state) => state.clearJob);
  const clearExtraSystemsJob = useJobExtraSystemsTemporaryStore((state) => state.clearJob);

  const handleCreateJob = async (formData: JobFormData) => {
    try {
      const mutationInput = mapFormDataToCreateInput(formData);
      const results = await createJob({
        variables: { job: mutationInput },
        refetchQueries: ['listJobs', 'GetRaptorJobs', 'listAvailableEquipmentsForTimeline'],
        awaitRefetchQueries: true
      });

      const mutationResult = results.data?.createRaptorJob;

      if (mutationResult && !mutationResult.isSuccess) {
        const errorData = mutationResult.error;
        throw createJobMutationError(errorData || {}, 'Job creation failed');
      }

      // Clear temporary crew members and extra systems from store after successful save
      const newJobId = results.data?.createRaptorJob?.value?.id;
      if (newJobId) {
        clearCrewJob(newJobId);
        clearExtraSystemsJob(newJobId);
      }
      // Also clear temporary data for unsaved jobs
      if (!formData.id || formData.id === 0) {
        clearCrewJob(0);
        clearExtraSystemsJob(0);
      }

      return results;
    } catch (err) {
      console.error('Error creating job:', err);
      throw err;
    }
  };

  return { handleCreateJob, data, loading, error };
};
